import { useEffect, useState } from "react"
import "./styles.scss"
import { engagementStore } from "src/modules/engagement/engagementStore"
import { formatCurrency } from "src/shared/utils/formatCurrency"
import { useItemRoute } from "src/modules/engagement/workflow/useItemRoute"
import { useEngagementBudget } from "./hooks/useEngagementBudget"
import { BudgetHeader } from "./components/BudgetHeader"
import { EngagementLaunchesTable } from "./components/EngagementLaunchesTable"
import { LaunchNetworksTable } from "./components/LaunchNetworksTable"

export const Budget = () => {
  const engagementId = engagementStore.selectedEngagement.id

  // The open launch lives in the URL, like the other drill-in items, so a
  // launch's budget can be linked and walked with the browser's back button.
  const [openLaunchId, setOpenLaunchId] = useItemRoute()
  const [editing, setEditing] = useState(false)

  const {
    header,
    tree,
    loading,
    error,
    setLaunchPolicy,
    setNetworkPolicy,
    setNetworkPolicyWithAudiences,
    setAudiencePolicy,
  } = useEngagementBudget(engagementId)

  if (loading) return <Spinner />
  if (error) return <ErrorState message={error} />
  if (!header || !tree) return <ErrorState message="No budget data" />

  const openLaunch = tree.launches.find((l) => l.launch_id === openLaunchId)

  // A stale deep link (launch deleted, or from another engagement) falls back
  // to the list rather than an empty pane.
  if (openLaunchId != null && !openLaunch) {
    return (
      <div className="budget-pane">
        <BackLink onClick={() => setOpenLaunchId(null)} />
        <div className="budget-empty">
          <div className="budget-empty__title">Campaign not found</div>
          <div className="budget-empty__body">
            This campaign is no longer part of the engagement's budget.
          </div>
        </div>
      </div>
    )
  }

  if (openLaunch) {
    return (
      <div className="budget-pane">
        <div className="budget-detail__head">
          <BackLink onClick={() => setOpenLaunchId(null)} />
          <span className="budget-detail__name">{openLaunch.launch_name}</span>
          <EditToggle editing={editing} onToggle={() => setEditing((e) => !e)} />
        </div>

        {/* The engagement's figures stay in view here: a campaign's networks
            are read against the balance and spend window they draw from. */}
        <div className="budget-strip">
          <BudgetHeader header={header} />
          <div className="budget-strip__rule" />
          <div className="budget-header">
            <span className="budget-strip__scope">This campaign</span>
            <Stat label="Amount" value={formatCurrency(openLaunch.resolved_amount)} />
            <div className="budget-header__stat" title="Held back from the campaign's gross before networks are paid.">
              <span className="budget-header__label">Margin</span>
              <HeaderMarginInput
                value={openLaunch.margin}
                editing={editing}
                onCommit={(margin) =>
                  setLaunchPolicy({
                    launch_id: openLaunch.launch_id,
                    percent: openLaunch.percent,
                    margin,
                  })
                }
              />
            </div>
          </div>
        </div>

        <div className="budget-card">
          <div className="budget-card__head">
            <span className="budget-card__title">Networks</span>
          </div>
          <LaunchNetworksTable
            launch={openLaunch}
            spendWindowDays={header.spend_window_days}
            editing={editing}
            onSetNetwork={(network, percent, dailyCap, bid) =>
              setNetworkPolicy(openLaunch.launch_id, {
                network,
                percent,
                daily_spend_cap: dailyCap,
                bid,
              })
            }
            onSetNetworkCap={(network, percent, dailyCap, bid, adGroups) =>
              setNetworkPolicyWithAudiences(
                openLaunch.launch_id,
                { network, percent, daily_spend_cap: dailyCap, bid },
                adGroups.map((a) => ({
                  adGroupId: a.adGroupId,
                  payload: {
                    network,
                    percent: a.percent,
                    daily_spend_cap: a.dailyCap,
                    bid: a.bid,
                  },
                })),
              )
            }
            onSetAudience={(adGroupId, network, percent, dailyCap, bid) =>
              setAudiencePolicy(openLaunch.launch_id, adGroupId, {
                network,
                percent,
                daily_spend_cap: dailyCap,
                bid,
              })
            }
          />
        </div>
      </div>
    )
  }

  return (
    <div className="budget-pane">
      <div className="budget-strip">
        <BudgetHeader header={header} />
      </div>

      {tree.launches.length === 0 ? (
        <div className="budget-empty">
          <div className="budget-empty__title">No campaigns yet</div>
          <div className="budget-empty__body">
            The engagement's remaining flight funding is split across its campaigns. Create a campaign first, then
            come back here to allocate it.
          </div>
        </div>
      ) : (
        <div className="budget-card">
          <div className="budget-card__head">
            <span className="budget-card__title">Campaigns</span>
            <span className="budget-card__count">{tree.launches.length}</span>
            <EditToggle editing={editing} onToggle={() => setEditing((e) => !e)} />
          </div>
          <EngagementLaunchesTable
            launches={tree.launches}
            funding={tree.remaining_flight_funding}
            editing={editing}
            onSelectLaunch={setOpenLaunchId}
            onSetPercent={(launchId, percent) => setLaunchPolicy({ launch_id: launchId, percent })}
          />
        </div>
      )}
    </div>
  )
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="budget-detail__back" onClick={onClick}>
      <i className="bi bi-chevron-left me-1" />
      Campaigns
    </button>
  )
}

function EditToggle({ editing, onToggle }: { editing: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      className="btn btn-sm btn-outline-primary ms-auto"
      style={{ fontSize: "0.72rem" }}
      onClick={onToggle}
    >
      <i className={`bi ${editing ? "bi-check-lg" : "bi-pencil"} me-1`} />
      {editing ? "Done" : "Edit"}
    </button>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="budget-header__stat">
      <span className="budget-header__label">{label}</span>
      <span className="budget-header__value">{value}</span>
    </div>
  )
}

function HeaderMarginInput({
  value,
  editing,
  onCommit,
}: {
  value: number
  editing: boolean
  onCommit: (v: number) => void
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const [local, setLocal] = useState(value)
  useEffect(() => setLocal(value), [value])

  const outOfRange = local < 0 || local > 100
  const round2 = (n: number) => Math.round(n * 100) / 100
  const cls =
    "budget-inline-input" +
    (!editing ? " budget-inline-input--readonly" : "") +
    (outOfRange && editing ? " budget-inline-input--error" : "")

  return (
    <div className={cls} style={{ display: "inline-flex" }}>
      <input
        type="text"
        inputMode="decimal"
        className="budget-inline-input__field"
        style={{ width: "4ch" }}
        readOnly={!editing}
        tabIndex={editing ? 0 : -1}
        value={draft !== null ? draft : local.toFixed(0)}
        onChange={(e) => {
          setDraft(e.target.value)
          const v = parseFloat(e.target.value)
          if (!isNaN(v)) setLocal(v)
        }}
        onFocus={() => editing && setDraft(local.toFixed(0))}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setDraft(null)
            setLocal(value)
            ;(e.target as HTMLInputElement).blur()
          } else if (e.key === "Enter") {
            (e.target as HTMLInputElement).blur()
          }
        }}
        onBlur={() => {
          setDraft(null)
          if (outOfRange) {
            setLocal(value)
            return
          }
          if (round2(local) !== round2(value)) onCommit(round2(local))
        }}
      />
      <span className="budget-inline-input__prefix">%</span>
    </div>
  )
}

function Spinner() {
  return (
    <div className="d-flex align-items-center justify-content-center py-5">
      <div className="spinner-border text-secondary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="budget-pane">
      <div className="budget-empty">
        <div className="budget-empty__title">Budget error</div>
        <div className="budget-empty__body">{message}</div>
      </div>
    </div>
  )
}
